/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useEffect, useState } from "react";

import {
    Alert,
    Autocomplete,
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Fade,
    Grid,
    IconButton,
    Modal,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";

import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { Asset, Effect, ModalBase, Nullable } from "../../utils/Interfaces";
import { delayedAction } from "../../utils/Functions";
import {
    AssetType,
    AssetTypeMap,
    assetTypeMappings,
    Condition,
    ConditionMap,
    DisposalMethod,
    DisposalMethodMap,
    disposalMethodMappings,
    DisposalReason,
    DisposalReasonMap,
    lookupAssetType,
    lookupCondition,
    lookupDisposalMethod,
    lookupDisposalReason,
    lookupValidConditionMappings,
    lookupValidDisposalReasonMappings,
} from "../../utils/Constants";
import CloseIcon from "@mui/icons-material/Close";

import { NumericFormat } from "react-number-format";
import { modifyAsset } from "./assetsFunctions";
import { useAppDispatch } from "../../app/hooks";

export interface ModifyAssetModalProps extends ModalBase {
    asset: Nullable<Asset>;
    onAssetModified: Effect;
}

const ModifyAssetModal = (props: ModifyAssetModalProps) => {
    const { open, handleClose, asset, onAssetModified } = props;
    const dispatch: Dispatch<AnyAction> = useAppDispatch();
    const theme = useTheme();
    const [loading, setLoading] = useState<boolean>(false);

    const [name, setName] = useState<string>("");
    const [manufacturer, setManufacturer] = useState<string>("");
    const [partNumber, setPartNumber] = useState<string>("");
    const [price, setPrice] = useState<number>(0.0);
    const [notes, setNotes] = useState<Nullable<string>>(null);
    const [serialNumber, setSerialNumber] = useState<Nullable<string>>(null);
    const [assetType, setAssetType] = useState<AssetType>(AssetType.TOOL);
    const [condition, setCondition] = useState<Condition>(Condition.EXCELLENT);
    const [disposalReason, setDisposalReason] = useState<DisposalReason>(DisposalReason.IN_SERVICE);
    const [disposalMethod, setDisposalMethod] = useState<DisposalMethod>(DisposalMethod.IN_SERVICE);
    const [disposalNotes, setDisposalNotes] = useState<Nullable<string>>(null);

    function resetAssetValues(): void {
        if (asset === null) {
            clearAssetValues();
            return;
        }

        setName(asset ? asset.name : "");
        setManufacturer(asset ? asset.manufacturer : "");
        setPartNumber(asset ? asset.part_number : "");
        setPrice(asset ? asset.price : 0.0);
        setNotes(asset ? asset.notes : "");
        setSerialNumber(asset ? asset.serial_number : "");
        setAssetType(asset ? asset.asset_type : AssetType.TOOL);
        setCondition(asset ? asset.condition : Condition.EXCELLENT);
        setDisposalReason(asset ? asset.disposal_reason : DisposalReason.IN_SERVICE);
        setDisposalMethod(asset ? asset.disposal_method : DisposalMethod.IN_SERVICE);
        setDisposalNotes(asset ? asset.disposal_notes : "");
    }

    function clearAssetValues(): void {
        setName("");
        setManufacturer("");
        setPartNumber("");
        setPrice(0.0);
        setNotes("");
        setSerialNumber("");
        setAssetType(AssetType.TOOL);
        setCondition(Condition.EXCELLENT);
        setDisposalReason(DisposalReason.IN_SERVICE);
        setDisposalMethod(DisposalMethod.IN_SERVICE);
        setDisposalNotes("");
    }

    function onClose() {
        handleClose();
        void delayedAction(500, () => {
            clearAssetValues();
        });
    }

    useEffect(() => {
        resetAssetValues();
    }, [asset]);

    useEffect(() => {
        if (open) resetAssetValues();
    }, [open]);

    function getFormErrors(): string {
        if (name === null || name.trim() === "") {
            return "Asset name cannot be blank.";
        }
        if (manufacturer === null || manufacturer.trim() === "") {
            return "Asset manufacturer cannot be blank.";
        }
        if (partNumber === null || partNumber.trim() === "") {
            return "Asset part number cannot be blank.";
        }

        if (disposalMethod === DisposalMethod.SPECIAL && (disposalNotes === null || disposalNotes.trim() === "")) {
            return "If the asset requires a special disposal method, please provide any supplemental notes or N/A in the 'Disposal Notes' section.";
        }

        if (disposalMethod === DisposalMethod.RETAINED && (disposalNotes === null || disposalNotes.trim() === "")) {
            return "If the asset is no longer in service, but is being retained, please provide location details in the 'Disposal Notes' section.";
        }

        return "";
    }

    function getFormWarnings(): string {
        // Only show warnings if errors aren't present.
        if (getFormErrors() !== "") {
            return "";
        }

        if (assetType !== AssetType.CONSUMABLE && (serialNumber === null || serialNumber.trim() === "")) {
            return "Where possible, serial numbers should be applied to non-consumables.";
        }

        if ((serialNumber?.toLowerCase().trim() ?? "") === "n/a" || (serialNumber?.toLowerCase().trim() ?? "") === "na") {
            return "It is recommended to leave the Serial # field blank if it's not applicable.";
        }

        return "";
    }

    return (
        <Modal
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
            open={open}
            onClose={onClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 500,
            }}
        >
            <Fade in={open}>
                <Box
                    sx={{
                        width: "100%",
                        maxHeight: "90vh",
                        maxWidth: "1800px",
                        backgroundColor: theme.palette.background.paper,
                        border: "1px solid #000",
                        boxShadow: theme.shadows[5],
                        padding: theme.spacing(2, 4, 3),
                        margin: theme.spacing(2, 4, 3),
                        zIndex: 1000,
                        overflowY: "auto",
                        position: "relative",
                    }}
                >
                    {asset === null && <Typography>Please select an asset to continue.</Typography>}

                    {asset !== null && (
                        <Box>
                            <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                                <Typography variant="h5" textAlign="center" sx={{ ml: 2, mb: 2, width: "100%" }}>
                                    Update Properties for {asset?.name} ({asset?.part_number})
                                </Typography>
                                <Stack direction="column" justifyContent="flex-start" sx={{ mr: -2 }}>
                                    <IconButton aria-label="delete" onClick={onClose}>
                                        <CloseIcon />
                                    </IconButton>
                                </Stack>
                            </Stack>

                            {getFormErrors() !== "" && <Alert severity="error">{getFormErrors()}</Alert>}

                            {getFormWarnings() !== "" && <Alert severity="warning">{getFormWarnings()}</Alert>}

                            {getFormErrors() === "" && getFormWarnings() === "" && (
                                <Alert severity="success">Form entries are valid.</Alert>
                            )}

                            <Grid container>
                                <Grid xs={6} sx={{ p: 1 }} item>
                                    <TextField
                                        fullWidth
                                        sx={{ m: 1 }}
                                        value={name}
                                        onChange={(event) => setName(event.target.value)}
                                        label="Asset Name"
                                        name="asset_name"
                                    />
                                </Grid>
                                <Grid xs={6} sx={{ p: 1 }} item>
                                    <TextField
                                        fullWidth
                                        sx={{ m: 1 }}
                                        value={manufacturer}
                                        onChange={(event) => setManufacturer(event.target.value)}
                                        label="Asset Manufacturer"
                                        name="asset_manufacturer"
                                    />
                                </Grid>
                                <Grid xs={6} sx={{ p: 1 }} item>
                                    <TextField
                                        fullWidth
                                        sx={{ m: 1 }}
                                        value={partNumber}
                                        onChange={(event) => setPartNumber(event.target.value)}
                                        label="Asset Part #"
                                        name="asset_part_number"
                                        InputProps={{ sx: { fontFamily: '"Tahoma", sans-serif' } }}
                                    />
                                </Grid>
                                <Grid xs={6} sx={{ p: 1 }} item>
                                    <TextField
                                        fullWidth
                                        sx={{ m: 1 }}
                                        value={serialNumber}
                                        onChange={(event) => setSerialNumber(event.target.value)}
                                        label="Asset Serial #"
                                        name="asset_serial_number"
                                        InputProps={{ sx: { fontFamily: '"Tahoma", sans-serif' } }}
                                    />
                                </Grid>
                                <Grid xs={6} sx={{ p: 1 }} item>
                                    <NumericFormat
                                        allowNegative={false}
                                        value={price}
                                        customInput={TextField}
                                        style={{ marginLeft: 8, marginTop: 8, width: "100%" }}
                                        label="Price"
                                        thousandSeparator=","
                                        decimalScale={2}
                                        prefix="$ "
                                        onValueChange={(values) => {
                                            setPrice(values.floatValue ?? 0.0);
                                        }}
                                    />
                                </Grid>

                                <Grid xs={6} sx={{ p: 1 }} item>
                                    <Autocomplete
                                        fullWidth
                                        disablePortal
                                        onChange={(_event, value) => setAssetType(value ? value.value : AssetType.TOOL)}
                                        id="asset_type"
                                        title="Asset Type"
                                        getOptionKey={(option: AssetTypeMap) => option.value}
                                        options={assetTypeMappings}
                                        value={lookupAssetType(assetType)}
                                        blurOnSelect={true}
                                        autoHighlight={true}
                                        getOptionLabel={(option: AssetTypeMap) => option.name}
                                        renderInput={(params) => (
                                            <TextField {...params} sx={{ m: 1 }} fullWidth label="Select Asset Type" />
                                        )}
                                    />
                                </Grid>

                                <Grid xs={12} sx={{ p: 1 }} item>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        sx={{ m: 1 }}
                                        value={notes}
                                        onChange={(event) => setNotes(event.target.value)}
                                        label="Asset Notes"
                                        name="asset_notes"
                                    />
                                </Grid>

                                <Grid xs={6} sx={{ p: 1 }} item>
                                    <Autocomplete
                                        fullWidth
                                        disablePortal
                                        onChange={(_event, value) => setCondition(value ? value.value : Condition.EXCELLENT)}
                                        id="asset_condition"
                                        title="Asset Condition"
                                        getOptionKey={(option: ConditionMap) => option.value}
                                        options={lookupValidConditionMappings(assetType)}
                                        value={lookupCondition(condition)}
                                        blurOnSelect={true}
                                        autoHighlight={true}
                                        getOptionLabel={(option: ConditionMap) => option.name}
                                        renderInput={(params) => (
                                            <TextField {...params} sx={{ m: 1 }} fullWidth label="Select Asset Condition" />
                                        )}
                                    />
                                </Grid>

                                <Grid xs={6} sx={{ p: 1 }} item>
                                    <Autocomplete
                                        fullWidth
                                        disablePortal
                                        onChange={(_event, value) =>
                                            setDisposalReason(value ? value.value : DisposalReason.IN_SERVICE)
                                        }
                                        id="asset_disposal_reason"
                                        title="Disposal Reason"
                                        getOptionKey={(option: DisposalReasonMap) => option.value}
                                        options={lookupValidDisposalReasonMappings(assetType)}
                                        value={lookupDisposalReason(disposalReason)}
                                        blurOnSelect={true}
                                        autoHighlight={true}
                                        getOptionLabel={(option: DisposalReasonMap) => option.name}
                                        renderInput={(params) => (
                                            <TextField {...params} sx={{ m: 1 }} fullWidth label="Select Asset Disposal Reason" />
                                        )}
                                    />
                                </Grid>

                                <Grid xs={6} sx={{ p: 1 }} item>
                                    <Autocomplete
                                        fullWidth
                                        disablePortal
                                        onChange={(_event, value) =>
                                            setDisposalMethod(value ? value.value : DisposalMethod.IN_SERVICE)
                                        }
                                        id="asset_disposal_method"
                                        title="Disposal Method"
                                        getOptionKey={(option: DisposalMethodMap) => option.value}
                                        options={disposalMethodMappings}
                                        value={lookupDisposalMethod(disposalMethod)}
                                        blurOnSelect={true}
                                        autoHighlight={true}
                                        getOptionLabel={(option: DisposalMethodMap) => option.name}
                                        renderInput={(params) => (
                                            <TextField {...params} sx={{ m: 1 }} fullWidth label="Select Asset Disposal Method" />
                                        )}
                                    />
                                </Grid>

                                <Grid xs={12} sx={{ p: 1 }} item>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        sx={{ m: 1 }}
                                        value={disposalNotes}
                                        onChange={(event) => setDisposalNotes(event.target.value)}
                                        label="Disposal Notes"
                                        name="disposal_notes"
                                    />
                                </Grid>
                            </Grid>

                            {loading && (
                                <Stack direction="row" justifyContent="center">
                                    <CircularProgress size={16} sx={{ mt: 2.7, mb: 2 }} />
                                </Stack>
                            )}

                            {!loading && (
                                <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
                                    <Tooltip title="Discard changes">
                                        <Button variant="contained" color="secondary" sx={{ mr: 1 }} onClick={onClose}>
                                            Cancel
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Update material">
                                        <Button
                                            disabled={getFormErrors() !== ""}
                                            variant="contained"
                                            color="success"
                                            sx={{ ml: 1 }}
                                            onClick={() => {
                                                let payload: Asset = {
                                                    id: asset ? asset.id : -1,
                                                    name: name.trim(),
                                                    manufacturer: manufacturer.trim(),
                                                    part_number: partNumber.trim(),
                                                    price: price,
                                                    serial_number: !serialNumber ? null : serialNumber.trim(),
                                                    asset_type: assetType,
                                                    notes: !notes ? null : notes.trim(),
                                                    condition: condition,
                                                    disposal_reason: disposalReason,
                                                    disposal_method: disposalMethod,
                                                    disposal_notes: !disposalNotes ? null : disposalNotes.trim(),
                                                };
                                                modifyAsset(
                                                    payload,
                                                    dispatch,
                                                    () => {
                                                        onAssetModified();
                                                        onClose();
                                                    },
                                                    setLoading
                                                );
                                            }}
                                        >
                                            Update Asset
                                        </Button>
                                    </Tooltip>
                                </Stack>
                            )}
                        </Box>
                    )}
                </Box>
            </Fade>
        </Modal>
    );
};

export default ModifyAssetModal;
